import type { TJSONSchema } from "@artifact-management/json-dynamic-form";
import AddIcon from "@mui/icons-material/Add";
import {
  Autocomplete,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  autocompleteClasses,
} from "@mui/material";
import { type FieldValues, type Path, type PathValue, useController } from "react-hook-form";
import type { IFormGenInputProps } from "../index";
import { type TJobApplyOption, isJobApplyOptionArray, useFormGenInputJobApply } from "./logics";
import { FormGenInputJobApplyModal } from "./modal";

export const FormGenInputJobApply = <Schema extends TJSONSchema, T extends FieldValues>({
  control,
  uiSchemaElement,
  jsonSchemaElement,
  context,
  multiple,
}: IFormGenInputProps<Schema, T>) => {
  const jobApplyOptions = jsonSchemaElement.jobApply;
  if (!jobApplyOptions) {
    throw new Error(`Missing 'jobApply' keyword on the field schema for scope: ${uiSchemaElement.scope}`);
  }
  const {
    autocompleteOpen,
    setAutocompleteOpen,
    handleNewApplyClick,
    options,
    loading,
    applyModalOpen,
    setApplyModalOpen,
    handleNewApply,
  } = useFormGenInputJobApply(jobApplyOptions, context);

  const { field, fieldState } = useController({
    control,
    name: uiSchemaElement.scope as unknown as Path<T>,
    defaultValue: (multiple ? [] : null) as PathValue<T, Path<T>>,
  });

  const handleConfirmNewApply = (createdApply: Parameters<typeof handleNewApply>[0]) => {
    const createdOption = handleNewApply(createdApply);
    if (multiple) {
      const fieldValue: unknown = field.value;
      const currentValue: TJobApplyOption[] = isJobApplyOptionArray(fieldValue) ? fieldValue : [];
      const nextValue: TJobApplyOption[] = [
        createdOption,
        ...currentValue.filter((option) => option.value !== createdOption.value),
      ];
      field.onChange(nextValue);
      return;
    }
    field.onChange(createdOption);
  };

  return (
    <>
      <Autocomplete<TJobApplyOption, boolean, false, false>
        open={autocompleteOpen}
        onOpen={() => setAutocompleteOpen(true)}
        onClose={() => setAutocompleteOpen(false)}
        value={field.value as TJobApplyOption | TJobApplyOption[] | null}
        onChange={(_, value) => {
          field.onChange(value);
        }}
        options={options}
        loading={loading}
        onBlur={field.onBlur}
        disabled={field.disabled}
        multiple={multiple}
        disableCloseOnSelect={multiple}
        limitTags={1}
        size="small"
        fullWidth
        getOptionLabel={(option) => option?.label ?? ""}
        isOptionEqualToValue={(option, value) => {
          if (!option || !value) {
            return false;
          }

          return option.value === value.value;
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={uiSchemaElement.label}
            name={field.name}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message}
            inputRef={field.ref}
          />
        )}
        renderOption={(props, option, { selected }) => {
          const { key, ...optionProps } = props;

          return (
            <MenuItem
              key={key}
              {...optionProps}
              component="li"
              selected={selected}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "start !important",
              }}
            >
              <Typography variant="subtitle2">{option.label}</Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "black.light",
                }}
              >
                {option.subText}
              </Typography>
            </MenuItem>
          );
        }}
        slots={{
          paper: ({ children, ...props }) => (
            <Paper
              {...props}
              sx={{
                [`& .${autocompleteClasses.listbox}`]: {
                  pb: 0,
                },
              }}
            >
              {children}

              <Divider sx={{ mx: 2, my: 1 }} />

              <MenuItem
                component="div"
                onClick={handleNewApplyClick}
                onMouseDown={(evt) => {
                  evt.preventDefault();
                  evt.stopPropagation();
                }}
                sx={{
                  px: 2,
                  py: 1,
                  mb: 1,
                }}
              >
                <Stack sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}>
                  <AddIcon color="primary" />
                  <Typography variant="subtitle1SemiBold" color="primary">
                    Renseigner une nouvelle embauche
                  </Typography>
                </Stack>
              </MenuItem>
            </Paper>
          ),
        }}
      />
      <FormGenInputJobApplyModal
        open={applyModalOpen}
        context={context}
        onConfirm={handleConfirmNewApply}
        onCancel={() => setApplyModalOpen(false)}
      />
    </>
  );
};
